import { useMelwaysLink } from '@seek/melways-react';
import { useTranslations } from '@vocab/react';
import {
  ButtonIcon,
  Divider,
  Heading,
  IconEdit,
  Spread,
  Stack,
  Text,
  TextLink,
} from 'braid-design-system';
import type { ComponentProps, FunctionComponent } from 'react';

import {
  type Qualification,
  type Role,
  type Skill,
  VisibilityLevel,
} from 'src/__generated__/graphql';
import { CareerHistoryEditor } from 'src/components/CareerHistory/CareerHistoryEditor';
import { CareerHistoryList } from 'src/components/CareerHistory/CareerHistoryList';
import { FirstRoleForm } from 'src/components/CareerHistory/FirstRoleForm';
import { EducationEditor } from 'src/components/Education/EducationEditor';
import { EducationList } from 'src/components/Education/EducationList';
import { LicenceEditor } from 'src/components/Licences/LicenceEditor';
import { LicencesList } from 'src/components/Licences/LicencesList';
import { SkillsEditor } from 'src/components/Skills/SkillsEditor';
import Routes from 'src/constants/routes';
import { useConfig } from 'src/context/config';
import {
  EditorDrawer,
  EditorDrawerProvider,
  useEditorDrawer,
} from 'src/ui/EditorDrawer/EditorDrawer';

import translations from './ProfileSummary.vocab';

export interface ProfileSummaryProps {
  advertiser: string;
  roles: Role[];
  unconfirmedRoles: Role[];
  qualifications?: Qualification[];
  unconfirmedQualifications: Qualification[];
  skills?: Skill[];
  licences?: any[];
  profilePrivacyLevel?: VisibilityLevel;
  isNewToWorkforce: boolean;
  onNewToWorkforceToggle: (toggle: boolean) => void;
}

const ProfileSummary: FunctionComponent<ProfileSummaryProps> = ({
  advertiser,
  skills = [],
  roles = [],
  unconfirmedRoles,
  qualifications = [],
  unconfirmedQualifications,
  licences = [],
  profilePrivacyLevel,
  isNewToWorkforce,
  onNewToWorkforceToggle,
}) => {
  const { BRAND: brand } = useConfig().config;
  const { t } = useTranslations(translations);

  const showHiddenBlurb = profilePrivacyLevel === VisibilityLevel.Hidden;
  const showStandardBlurb =
    profilePrivacyLevel === VisibilityLevel.Standard ||
    profilePrivacyLevel === VisibilityLevel.Limited;
  const profilePrivacyRoute = useMelwaysLink(Routes.PROFILE_PRIVACY);

  return (
    <Stack space="large">
      <Heading level="3">{t('Your SEEK Profile', { brand })}</Heading>
      <Text>
        {t(
          'When you apply for a job, your Profile and any verified credentials will be accessed by advertiser job as part of your job application',
          { brand, advertiser },
        )}
      </Text>

      <Stack space="medium">
        <Spread space="xsmall">
          <Stack space="medium">
            <Text weight="strong">{t('Career history')}</Text>
            <Text>{t('roles-quantity', { count: roles.length })}</Text>
          </Stack>
          <EditorDrawerProvider name="career-history">
            <EditButton
              data={{ testid: 'profile-summary-item-edit-button' }}
              id="edit-career-history"
              label={t('Edit')}
            />
            <EditorDrawer title={t('Career history')} name="career-history">
              {roles.length > 0 || unconfirmedRoles.length > 0 ? (
                <>
                  <CareerHistoryList
                    unconfirmedRoles={unconfirmedRoles}
                    roles={roles}
                    hideWhenEditing
                  />
                  <CareerHistoryEditor
                    unconfirmedRoles={unconfirmedRoles}
                    roles={roles}
                  />
                </>
              ) : (
                <FirstRoleForm
                  isNewToWorkforce={isNewToWorkforce}
                  onNewToWorkforceToggle={onNewToWorkforceToggle}
                  roles={roles}
                  unconfirmedRoles={unconfirmedRoles}
                />
              )}
            </EditorDrawer>
          </EditorDrawerProvider>
        </Spread>
        <Divider />
        <Spread space="xsmall">
          <Stack space="medium">
            <Text weight="strong">{t('Education')}</Text>
            <Text>
              {t('qualifications-quantity', { count: qualifications.length })}
            </Text>
          </Stack>
          <EditorDrawerProvider name="education">
            <EditButton
              data={{ testid: 'profile-summary-item-edit-button' }}
              id="edit-education"
              label={t('Edit')}
            />
            <EditorDrawer title={t('Education')} name="education">
              <EducationList
                qualifications={qualifications}
                unconfirmedQualifications={unconfirmedQualifications}
                shouldShowPrompt
                hideWhenEditing
              />
              <EducationEditor
                qualifications={qualifications}
                unconfirmedQualifications={unconfirmedQualifications}
              />
            </EditorDrawer>
          </EditorDrawerProvider>
        </Spread>
        <Divider />
        <Spread space="xsmall">
          <Stack space="medium">
            <Text weight="strong">{t('Licences certifications')}</Text>
            <Text>{t('credentials-quantity', { count: licences.length })}</Text>
          </Stack>
          <EditorDrawerProvider name="licences">
            <EditButton
              data={{
                testid: 'profile-summary-item-edit-licences-button',
              }}
              id="edit-licences"
              label={t('Edit licence or certification')}
            />
            <EditorDrawer name="licences" title={t('Licences certifications')}>
              <LicencesList licences={licences} hideWhenEditing />
              <LicenceEditor licences={licences} />
            </EditorDrawer>
          </EditorDrawerProvider>
        </Spread>
        <Divider />
        <Spread space="xsmall">
          <Stack space="medium">
            <Text weight="strong">{t('Skills')}</Text>
            <Text>{t('skills-quantity', { count: skills.length })}</Text>
          </Stack>
          <EditorDrawerProvider name="skills">
            <EditButton
              data={{ testid: 'profile-summary-item-edit-skills-button' }}
              id="edit-skills"
              label={t('Edit skills')}
            />
            <EditorDrawer title={t('Edit skills')} name="skills">
              <SkillsEditor skills={skills} />
            </EditorDrawer>
          </EditorDrawerProvider>
        </Spread>
      </Stack>
      <Divider />
      {showHiddenBlurb && (
        <Text size="small">
          {t('When you apply on Hidden', {
            brand,
          })}{' '}
          <TextLink weight="weak" href={profilePrivacyRoute} target="_blank">
            {t('Learn more')}
          </TextLink>
        </Text>
      )}
      {showStandardBlurb && (
        <Text size="small" tone="secondary">
          {t('When you apply on Standard or Limited', {
            brand,
          })}{' '}
          <TextLink weight="weak" href={profilePrivacyRoute} target="_blank">
            {t('Learn more')}
          </TextLink>
        </Text>
      )}
    </Stack>
  );
};

export default ProfileSummary;

const EditButton = (
  props: Omit<ComponentProps<typeof ButtonIcon>, 'tone' | 'variant' | 'icon'>,
) => {
  const { onEditorOpen } = useEditorDrawer();
  return (
    <ButtonIcon
      tone="neutral"
      variant="transparent"
      icon={<IconEdit />}
      bleed
      onClick={(e) => {
        onEditorOpen();
        props.onClick?.(e);
      }}
      {...props}
    />
  );
};
